// The pressure haze of the fast client (R499): slow banded turbidity that
// thickens toward the edges of the world and drifts almost imperceptibly, so
// the map acquires a horizon instead of a wall (R101).
//
// This is the port of `render/haze.ts`. It is WORLD space — it sits over the
// map and pans with it, unlike the background gradient and the vignette,
// which are screen passes (ui-map-interface.md §7). Every dimension is
// derived from `WORLD` and `WORLD_H`, so a map of any size or shape hazes
// correctly with no figure to update.
//
// Eight quads: two bands over each of the four edges. A band is one instance
// of the `haze-near` or `haze-far` atlas cell, which bakes the banded ramp
// AND its band alpha into its own texels (`gfx/atlas.ts`), stretched along the
// edge. So the instance is written white at alpha 1, and the two bands are
// told apart by their cell and by their depth, not by an alpha the layer
// writes.
//
// The quad is placed the way PixiJS places a sprite of `anchor(0.5, 0)`: the
// instance offset is the box centre in the pre-rotation local frame
// (`gfx/quads.ts` `QUAD_VERTEX`), so an offset of `d/2` on y puts the opaque
// head of the ramp on the anchor line and hangs the rest inward.
//
// One deliberate difference from the PixiJS layer: a band whose rectangle
// misses the frame's cull rect packs no instance. The PixiJS layer does not
// cull. A band is a huge translucent fill, and the fill is the whole cost of
// this piece, so an edge off screen must cost nothing.

import {
  HAZE_DEPTH_FRAC, HAZE_DRIFT_PERIOD_SEC, HAZE_DRIFT_PX, HAZE_FAR_FRAC,
  HAZE_OVERHANG_FRAC,
} from '../../gen/presentation';
import { WORLD, WORLD_H } from '../../world';
import type { AtlasEntry } from '../gfx/atlas';
import { outside, type DrawCtx, type FrameInfo, type Layer } from '../layer';
import type { BatchSource, QuadSink } from './batches';

/** Two bands over four edges, which is the most the layer ever writes. */
const MAX_BANDS = 8;

/** How many edges the world rectangle has. It is also the phase divisor. */
const EDGES = 4;

/**
 * One edge: where the opaque line sits, which way the haze grows, and how
 * long the band along it is. The world is a rectangle, so the top and the
 * bottom bands run the width and the two side bands run the height (R395).
 * The rotation maps the cell's local +y (opaque to clear) onto the inward
 * normal, and local +x runs along the edge, centred
 * (`render/haze.ts:106-111`).
 */
interface Edge { x: number; y: number; rot: number; nx: number; ny: number; span: number }

export class HazeLayer implements Layer {
  readonly name = 'haze';

  private readonly quads: QuadSink;
  private readonly cells: readonly [AtlasEntry, AtlasEntry];
  private shown = true;

  // The bands as flat arrays. `rebuild` writes them; `update` reads them and
  // allocates nothing.
  private readonly nx = new Float32Array(MAX_BANDS);
  private readonly ny = new Float32Array(MAX_BANDS);
  private readonly bx = new Float32Array(MAX_BANDS);
  private readonly by = new Float32Array(MAX_BANDS);
  private readonly span = new Float32Array(MAX_BANDS);
  private readonly depth = new Float32Array(MAX_BANDS);
  private readonly rot = new Float32Array(MAX_BANDS);
  private readonly period = new Float32Array(MAX_BANDS);
  private readonly phase = new Float32Array(MAX_BANDS);
  /** Which cell a band draws with: 0 near, 1 far. */
  private readonly cell = new Uint8Array(MAX_BANDS);
  private bands = 0;

  constructor(src: BatchSource) {
    this.quads = src.newQuads(MAX_BANDS);
    this.cells = [src.sprite('haze-near'), src.sprite('haze-far')];
    // The PixiJS layer builds in its constructor, before any socket exists,
    // and rebuilds when the hello gave a different grid (R261). Two bands is
    // what the client ships and what an unstated `hazeb` knob means.
    this.rebuild(2);
  }

  /**
   * Builds the bands for the world rectangle as it is now. The integrator
   * calls it on `engine.onGrid` and on a `hazeb` change. `bands === 1` keeps
   * the near band alone, which is the cheap half of the fill.
   */
  rebuild(bands: 1 | 2): void {
    // The overhang and the depth are fractions of the width on any world, so
    // a wide map hazes at the same thickness on all four edges.
    const out = WORLD * HAZE_OVERHANG_FRAC;
    const spanW = WORLD + 2 * out;
    const spanH = WORLD_H + 2 * out;
    const depth = WORLD * HAZE_DEPTH_FRAC;
    const edges: Edge[] = [
      { x: WORLD / 2, y: 0, rot: 0, nx: 0, ny: 1, span: spanW },                  // top
      { x: WORLD / 2, y: WORLD_H, rot: Math.PI, nx: 0, ny: -1, span: spanW },     // bottom
      { x: 0, y: WORLD_H / 2, rot: -Math.PI / 2, nx: 1, ny: 0, span: spanH },     // left
      { x: WORLD, y: WORLD_H / 2, rot: Math.PI / 2, nx: -1, ny: 0, span: spanH }, // right
    ];
    let n = 0;
    for (let i = 0; i < bands; i++) {
      const d = (i === 0 ? depth : depth * HAZE_FAR_FRAC) + out;
      for (let k = 0; k < edges.length; k++) {
        const e = edges[k];
        this.nx[n] = e.nx;
        this.ny[n] = e.ny;
        // The band starts `out` px outside the world, so its thickest part is
        // over the void and the boundary itself carries no edge.
        this.bx[n] = e.x - e.nx * out;
        this.by[n] = e.y - e.ny * out;
        this.span[n] = e.span;
        this.depth[n] = d;
        this.rot[n] = e.rot;
        this.period[n] = HAZE_DRIFT_PERIOD_SEC[i];
        // Each edge starts at its own point in the cycle, so the four never
        // breathe together and read as one pulsing frame
        // (`render/haze.ts:131`; the 1.9 offset between the bands is a
        // literal of that line).
        this.phase[n] = (k / EDGES) * Math.PI * 2 + i * 1.9;
        this.cell[n] = i;
        n++;
      }
    }
    this.bands = n;
  }

  /** How many bands are built. The tests read it. */
  get count(): number { return this.bands; }

  /**
   * Eight instances and nothing else — no geometry is rebuilt, ever. The
   * drift is meant to sit at the edge of perception: a 16 px excursion over a
   * 47-second cycle is about 2 px a second at most.
   */
  update(frame: FrameInfo): void {
    const q = this.quads;
    q.begin();
    const cull = frame.cull;
    for (let i = 0; i < this.bands; i++) {
      const nx = this.nx[i];
      const ny = this.ny[i];
      const k = Math.sin((frame.timeS / this.period[i]) * Math.PI * 2 + this.phase[i]) * HAZE_DRIFT_PX;
      const x = this.bx[i] + nx * k;
      const y = this.by[i] + ny * k;
      const d = this.depth[i];
      const span = this.span[i];
      // Every rotation is a multiple of a quarter turn, so the band's own
      // rectangle is axis-aligned: its centre sits `d/2` along the inward
      // normal, and it reaches `span/2` along the edge. Growing the cull rect
      // by those half-extents turns the point test into the rectangle
      // overlap the fill actually needs.
      const cx = x + nx * (d / 2);
      const cy = y + ny * (d / 2);
      const hx = nx === 0 ? span / 2 : d / 2;
      const hy = ny === 0 ? span / 2 : d / 2;
      if (outside(cx, cy, cull.x0 - hx, cull.y0 - hy, cull.x1 + hx, cull.y1 + hy)) continue;
      const e = this.cells[this.cell[i]];
      q.quad(x, y, 0, d / 2, span, d, this.rot[i], 0, 0xffffff, 1, e.u0, e.v0, e.u1, e.v1);
    }
  }

  draw(ctx: DrawCtx): void {
    this.quads.flush(ctx.cam);
  }

  setVisible(v: boolean): void { this.shown = v; }
  get visible(): boolean { return this.shown; }
}
